import { SmallButton, InputCurrency } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { calculateTotal, formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputContainer } from "@sellar-io/components";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderUpdate } from "../../../hooks/api/v2/order/useOrderUpdate";
import { usePriceFormat } from "../../../hooks/usePriceFormat";
import { AlertCompact } from "../../../components/alert/AlertCompact";

export const OrderEditSummaryRow = ({
  name,
  value,
  onEdit,
}: {
  name: string;
  value: string;
  onEdit?: () => void;
}) => {
  return (
    <>
      <div>{name}</div>
      <div className={"text-right"}>{value}</div>
      {onEdit ? (
        <div onClick={onEdit} className={"cursor-pointer"}>
          <Icon icon={"PENCIL"} />
        </div>
      ) : (
        <div />
      )}
    </>
  );
};

export const OrderEditSummaryRowDiscount = ({
  discountAmount,
  calculatedDiscountAmount,
  orderId,
  onSuccess,
  order,
  setShowCheckoutIncreaseAlert,
}: {
  discountAmount: number;
  calculatedDiscountAmount: number;
  orderId: number;
  onSuccess?: () => void;
  setShowCheckoutIncreaseAlert: (showCheckoutIncreaseAlert: boolean) => void;
  order: OrderGetResponseType;
}) => {
  const [editing, setEditing] = useState(false);

  const orderUpdate = useOrderUpdate(orderId);

  const update = (newValue: number | null) => {
    const newTotal = calculateTotal({
      orderItems: order.OrderItems,
      deliveryCharge: order.deliveryCharge,
      discountAmount: newValue ?? 0,
      vatPercentage: order.vatPercentage,
    });

    if (newTotal > order.paidAmount && order.paymentType === "checkout") {
      return setShowCheckoutIncreaseAlert(true);
    }
    orderUpdate.mutate(
      {
        discountAmount: newValue ?? 0,
      },
      {
        onSuccess: () => {
          setEditing(false);
          onSuccess?.();
          setShowCheckoutIncreaseAlert(false);
        },
      },
    );
  };

  const recalculateAlert = () => {
    if (discountAmount === calculatedDiscountAmount) return;
    return (
      <AlertCompact
        type="information"
        className={"col-span-3 text-sm"}
        onClick={() => update(calculatedDiscountAmount)}
      >
        <p className="mr-auto font-semibold">Volume discounts may apply</p>
        <span className="font-bold text-indigo-800 uppercase">Recalculate</span>
        <Icon icon="REFRESH" className="text-indigo-800" />
      </AlertCompact>
    );
  };

  if (orderUpdate.isPending)
    return (
      <div className={"col-span-3"}>
        <SpinnerLegacy />
      </div>
    );

  if (editing)
    return (
      <>
        {orderUpdate.isError && (
          <Alert
            className={"col-span-3"}
            type={"error"}
            message={
              apiV2AssertError(orderUpdate.error)?.response?.data.message ?? ""
            }
          />
        )}
        <OrderEditSummaryRowEdit
          value={discountAmount}
          onCancel={() => setEditing(false)}
          onUpdate={(updatedValue) => update(updatedValue)}
          name={"Discount"}
        />
        {recalculateAlert()}
      </>
    );

  return (
    <>
      <OrderEditSummaryRow
        name={"Discount"}
        value={formatCurrency(-discountAmount)}
        onEdit={() => setEditing(true)}
      />
      {recalculateAlert()}
    </>
  );
};

export const OrderEditSummaryRowDelivery = ({
  deliveryCharge,
  calculatedDeliveryCharge,
  orderId,
  onSuccess,
  setShowCheckoutIncreaseAlert,
  order,
}: {
  deliveryCharge: number;
  calculatedDeliveryCharge: number;
  orderId: number;
  onSuccess?: () => void;
  setShowCheckoutIncreaseAlert: (showCheckoutIncreaseAlert: boolean) => void;
  order: OrderGetResponseType;
}) => {
  const [editing, setEditing] = useState(false);

  const orderUpdate = useOrderUpdate(orderId);

  const update = (newValue: number | null) => {
    const newTotal = calculateTotal({
      orderItems: order.OrderItems,
      deliveryCharge: newValue ?? 0,
      discountAmount: order.discountAmount,
      vatPercentage: order.vatPercentage,
    });

    if (newTotal > order.paidAmount && order.paymentType === "checkout") {
      return setShowCheckoutIncreaseAlert(true);
    }
    orderUpdate.mutate(
      {
        deliveryCharge: newValue ?? 0,
      },
      {
        onSuccess: () => {
          setEditing(false);
          onSuccess?.();
          setShowCheckoutIncreaseAlert(false);
        },
      },
    );
  };

  const recalculateAlert = () => {
    if (deliveryCharge === calculatedDeliveryCharge) return;
    return (
      <AlertCompact
        type="information"
        className={"col-span-3 text-sm"}
        onClick={() => update(calculatedDeliveryCharge)}
      >
        <p className="mr-auto font-semibold">
          Delivery charges may have changed
        </p>
        <span className="font-bold text-indigo-800 uppercase">Recalculate</span>
        <Icon icon="REFRESH" className="text-indigo-800" />
      </AlertCompact>
    );
  };

  if (orderUpdate.isPending)
    return (
      <div className={"col-span-3"}>
        <SpinnerLegacy />
      </div>
    );

  if (editing)
    return (
      <>
        {orderUpdate.isError && (
          <Alert
            className={"col-span-3"}
            type={"error"}
            message={
              apiV2AssertError(orderUpdate.error)?.response?.data.message ?? ""
            }
          />
        )}
        <OrderEditSummaryRowEdit
          onCancel={() => setEditing(false)}
          onUpdate={(updatedValue) => update(updatedValue)}
          value={deliveryCharge}
          name={"Delivery"}
        />
        {recalculateAlert()}
      </>
    );

  return (
    <>
      <OrderEditSummaryRow
        name={"Delivery"}
        value={formatCurrency(deliveryCharge)}
        onEdit={() => setEditing(true)}
      />
      {recalculateAlert()}
    </>
  );
};

export const OrderEditSummaryRowEdit = ({
  onCancel,
  onUpdate,
  name,
  value: initValue,
}: {
  onCancel: () => void;
  onUpdate: (updatedValue: number | null) => void;
  name: string;
  value?: number;
}) => {
  const priceFormat = usePriceFormat(initValue);

  return (
    <>
      <div>{name}</div>
      <div className={"col-span-2"}>
        <InputContainer>
          <InputCurrency
            showPrefix
            allowNegative
            min={0}
            value={priceFormat.inputFormat}
            onChange={(e) => priceFormat.setInputFormat(e.target.value)}
          />
        </InputContainer>
      </div>
      <div className={"col-span-3 flex justify-end gap-2"}>
        <SmallButton color={"secondary"} onClick={onCancel}>
          Cancel
        </SmallButton>
        <SmallButton
          disabled={
            !priceFormat.inputFormat ||
            initValue === priceFormat.apiFormat ||
            (!!priceFormat.apiFormat && priceFormat.apiFormat < 0)
          }
          onClick={() => onUpdate(priceFormat.apiFormat)}
        >
          Update
        </SmallButton>
      </div>
    </>
  );
};
